import React, { useState } from 'react';
import messageService from '../../services/endpoints/messageService';

const PREFILLS = [
  'Please check in today',
  'Need to chat. Message me when you get this',
  'Lower steps today',
  'Cheat meal today',
] as const;

interface Props {
  athleteName: string;
  relationshipId: string;
  onClose: () => void;
}

const QuickMessageDrawer: React.FC<Props> = ({ athleteName, relationshipId, onClose }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    setError(null);
    setSent(false);
    try {
      await messageService.sendMessage({ relationshipId, body: message.trim() });
      setMessage('');
      setSent(true);
    } catch {
      setError('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-800 border-l border-dark-700 z-50 flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-dark-700 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Quick Message</h2>
            <p className="text-sm text-gray-400 mt-0.5">{athleteName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors text-xl leading-none mt-0.5"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Pre-fill chips */}
          <div>
            <p className="text-xs text-gray-500 mb-2">Quick replies</p>
            <div className="flex flex-col gap-2">
              {PREFILLS.map(text => (
                <button
                  key={text}
                  onClick={() => { setMessage(text); setSent(false); }}
                  className="text-left text-xs px-3 py-2 rounded-lg border border-dark-600 bg-dark-700 text-gray-300 hover:border-gold-500/50 hover:text-white transition-colors"
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          {/* Compose area */}
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Message</p>
            <textarea
              value={message}
              onChange={e => { setMessage(e.target.value); setSent(false); }}
              placeholder="Type a message…"
              rows={4}
              className="w-full bg-dark-900 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 resize-none"
            />
            {sent  && <p className="text-green-400 text-xs">Message sent.</p>}
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <div className="flex justify-end">
              <button
                onClick={handleSend}
                disabled={sending || !message.trim()}
                className="px-5 py-2 bg-gold-500 hover:bg-gold-400 disabled:bg-dark-700 disabled:text-gray-500 text-black text-sm font-semibold rounded-lg transition-colors"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickMessageDrawer;
